import { InteractionItem } from 'chart.js';
import moment from 'moment';
import React, { useMemo } from 'react';
import { useTranslation } from '../../../contexts/translation';
import { ThemeType, useLocalTheme } from '../../../hooks/useLocalTheme';
import { ChartsContainer } from '../../../pages/Charts';
import { ChartDataSet, ChartTypes } from '../../../types/app/chart.types';
import { ChartsDataProcessProps, DateTimeIntegerProcess } from '../../../types/app/dataProcess';
import { PerformanceComment } from '../../../types/app/performanceComment.types';
import { hoursMinutesShowFormat, utcFormat } from '../../../types/core/date.types';
import {
  BLUE,
  DARK_BLUE,
  getColorForColumn,
  getColorForHistoryColumn,
} from '../../../utils/core/functions/colors.function';
import { STORAGE_KEYS } from '../../../utils/core/localStorageKeys';

/**
 * Charts data process for performance dashboard.
 * xAxis: datetime
 * yAxis: number value
 * intervals: datetime
 * @param param0
 * @returns
 */
export function ChartsDataProcessProfilerPerformance({
  title,
  data,
  configs,
  intervals,
  chartsConfig,
  granularity,
  chartHeight,
  chartWidth,
  timezone,
  dashboardConfig,
  onChartClick,
  commentList1,
  commentList2,
}: ChartsDataProcessProps) {
  const { lang } = useTranslation();
  const { localTheme } = useLocalTheme();
  /**
   * Returns value from data array by datetime. If not found, 0 is returned.
   * @param time
   * @returns
   */
  const findValueInDataByTime = (
    time: string,
    iFrom: number,
    iTo: number,
    valueKey: string,
    // @ts-ignore
  ): number => data.slice(iFrom, iTo + 1).find((x) => x.time === time)?.[valueKey] || 0;

  // TODO. summary
  const findAllValuesByConfig = (value: string) => {
    return configs.map((config) => {
      return {
        id: config.dataSetId,
        value: findValueInDataByTime(value, config.iFrom, config.iTo, config.dataSetValueKey),
      };
    });
  };

  /**
   * Calculate and save data for chart based on settings in props of this component.
   * Calculated data are used for visualisation and for unique identification of each value.
   */
  const processedData = useMemo(() => {
    let result: DateTimeIntegerProcess[] = [];
    intervals.map((interval) => {
      // Start of interval
      let currDate = interval.start.clone();
      // Result datastructure. First value is start date.
      const startTime = currDate.format(utcFormat);
      result = [
        ...result,
        {
          time: startTime,
          timeObject: moment(startTime).utc(),
          values: findAllValuesByConfig(startTime),
        },
      ];
      // From start to end, step is granularity in minutes
      while (currDate.add(granularity, 'minutes').diff(interval.end) <= 0) {
        const time = currDate.format(utcFormat);
        // We need to find value in data for current datetime
        result = [
          ...result,
          {
            time,
            timeObject: moment(time).utc(),
            values: findAllValuesByConfig(time),
          },
        ];
      }
    });
    return result;
  }, [data, lang]);

  /**
   * Generates chart labels based on processed data
   */
  const chartLabels: string[] = useMemo(() => {
    let toReturn: string[] = [];
    processedData.forEach((item, i) => {
      const startStr = item.timeObject
        ?.clone()
        .add(-1, 'hours')
        .tz(timezone)
        .format(hoursMinutesShowFormat);
      const endStr = item.timeObject?.tz(timezone).format(hoursMinutesShowFormat);
      toReturn = [...toReturn, `${startStr} - ${endStr}`];
    });
    return toReturn;
  }, [processedData]);

  let chartsData: any[] = [];
  chartsData = chartsConfig!.map((config) => {
    return {
      ...config,
      dataSetId: config.dataSetId,
      chartType: config.type,
      data: [
        ...processedData.map((item) => {
          const value = item.values.filter((x) => x.id === config.dataSetId)[0].value;

          if (config.dataSetId === 'TAKT_TIME') {
            if (!dashboardConfig || dashboardConfig.taktTimeLineQty <= 0) return;
            return dashboardConfig.taktTimeLineQty;
          }
          if (
            config.dataSetId === 'ORANGE_THRESHOLD' &&
            dashboardConfig &&
            dashboardConfig.showOrangeLevelLine
          )
            return value * dashboardConfig.qtyLevelOrange;
          if (
            config.dataSetId === 'GREEN_THRESHOLD' &&
            dashboardConfig &&
            dashboardConfig.showGreenLevelLine
          )
            return value * dashboardConfig.qtyLevelGreen;
          if (config.dataSetId === 'IS_COMMENT') {
            if (
              (commentList1 &&
                commentList1?.some((comment: PerformanceComment) => comment.time === item.time)) ||
              (commentList2 &&
                commentList2?.some((comment: PerformanceComment) => comment.time === item.time))
            ) {
              // posunutí koment trojuhelniku
              return value + value * 0.035;
            } else {
              return;
            }
          }
          return value;
        }),
      ],
      colors: [
        ...processedData.map((item) => {
          // When plan, always return blue color
          if (config.dataSetId === 'PLAN') return localTheme === ThemeType.DARK ? DARK_BLUE : BLUE;
          // Color of current column is calculated depending on plan and current value
          const intervalStart = item.timeObject?.clone().utc().add(-granularity, 'minutes');
          const intervalEnd = item.timeObject?.clone().utc();
          const isCurrentInterval = moment.utc().isBetween(intervalStart, intervalEnd);
          const value = item.values.filter((x) => x.id === config.dataSetId)[0].value;
          const plan = item.values.filter((y) => y.id === 'PLAN')[0].value;
          if (
            config.isCurrent &&
            intervalStart &&
            intervalEnd &&
            dashboardConfig &&
            isCurrentInterval
          ) {
            let currentColor = getColorForColumn(
              value,
              plan,
              granularity,
              4,
              intervalStart,
              dashboardConfig.qtyLevelOrange,
              dashboardConfig.qtyLevelGreen,
              localTheme,
            );

            // set actual block color to local storage for stat color
            localStorage.setItem(STORAGE_KEYS.ACTUAL_BLOCK_COLOR, JSON.stringify(currentColor));
            return currentColor;
          } else {
            return getColorForHistoryColumn(
              value,
              plan,
              dashboardConfig?.qtyLevelOrange,
              dashboardConfig?.qtyLevelGreen,
              localTheme,
            );
          }
        }),
      ],
    };
  });

  const chartsDataSets: ChartDataSet[] = chartsData.map((item) => {
    return {
      chartType: item.chartType,
      style: item.style,
      label: item.label,
      colors: item.colors,
      x: chartLabels,
      y: item.data,
    };
  });

  const onClick = (result: InteractionItem[]) => {
    if (onChartClick) onChartClick(result, processedData);
  };

  return (
    <ChartsContainer
      chartData={chartsDataSets}
      labels={chartLabels}
      chartHeight={chartHeight ? chartHeight : undefined}
      chartWidth={chartWidth ? chartWidth : undefined}
      displayLegend={false}
      onClick={onClick}
      localTheme={localTheme}
    />
  );
}
